/**
 * ChatDocumentFlow — 雇用書類を提出
 *
 * Bubbles appear one by one (staggered) like a real chat instead of all at once.
 * Answer block shows only after the last assistant message is revealed.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { fetchChatFlow, postChatFlowDocumentChoice, postChatFlowDocumentUpload, type ChatFlowResponse, type VariousCopyUploadMode } from '../../../../services/chatFlowApi';
import { allowsVariousCopySkip, requiresBothSidesUpload, requiresOriginalToHeadOffice } from '../../../../catalog/variousCopyDocCodes';
import { getEmployeeEmail } from '../../auth/employeeAuthStorage';
import robotAvatar from '../../../../assests/icons/robot.png';
import attachIcon from '../../../../assests/icons/attach.png';
import sendIcon from '../../../../assests/icons/send.png';
import cautionIcon from '../../../../assests/icons/caution.png';
import continueIcon from '../../../../assests/icons/continue.png';
import PdfCanvasViewer from '../../documents/pdfPreview/PdfCanvasViewer';
import CardCameraCapture, { type CardCameraSide } from './CardCameraCapture';
import './ChatDocumentFlow.css';

export interface ChatDocumentFlowProps {
  onBack: () => void;
  /** Called when user taps はい、準備できました  */
  onReadyConfirmed?: () => void;
  /** Called when user taps 続ける after completing all documents (Step 1) */
  onContinue?: () => void;
}

type LoadState = 'loading' | 'ready' | 'empty' | 'error';

/** Delay between each bubble appearing (ms) — feels like messages arriving */
const BUBBLE_DELAY_MS = 750;
const AFTER_LAST_BUBBLE_MS = 400;

function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function AssistantRow({
  children,
  variant = 'grey',
  animateIn = false,
}: {
  children: React.ReactNode;
  variant?: 'grey' | 'warn';
  /** When true, play enter animation (opacity + slight slide) */
  animateIn?: boolean;
}) {
  return (
    <div className={`chat-document-flow__row ${animateIn ? 'chat-document-flow__row--enter' : ''}`}>
      <div className="chat-document-flow__avatar-wrap" aria-hidden>
        <img src={robotAvatar} alt="" className="chat-document-flow__avatar" />
      </div>
      <div className={`chat-document-flow__bubble-wrap chat-document-flow__bubble-wrap--${variant}`}>
        <div className={`chat-document-flow__bubble chat-document-flow__bubble--${variant}`}>{children}</div>
      </div>
    </div>
  );
}

const ChatDocumentFlow: React.FC<ChatDocumentFlowProps> = ({ onBack, onReadyConfirmed, onContinue }) => {
  const { t, language } = useLanguage();
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [flow, setFlow] = useState<ChatFlowResponse | null>(null);
  /** How many chat segments are visible (staggered reveal) */
  const [revealedCount, setRevealedCount] = useState(0);
  /** Show footer answers only after all bubbles shown */
  const [showAnswers, setShowAnswers] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);
  /** After user taps はい、準備できました — show next part of conversation */
  const [readyConfirmed, setReadyConfirmed] = useState(false);
  /** Staggered segments after ready: 1=user bubble, 2=afterReady, 3=first doc prompt */
  const [afterReadyStep, setAfterReadyStep] = useState(0);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [choiceBusy, setChoiceBusy] = useState(false);
  /** Each choice the user made (label shown in chat), in order. Upload entries include fileName + docName for the upload bubble. */
  const [choiceHistory, setChoiceHistory] = useState<{
    docIndex: number;
    label: string;
    fileName?: string;
    docName?: string;
    docCode?: string;
    choiceStatus?: 'send_later' | 'bring_when_boarding' | 'notyet' | 'skipped';
  }[]>([]);
  /** Index of last "Document N" assistant line we've revealed — delay next one so user bubble shows first */
  const [assistantRevealedUpTo, setAssistantRevealedUpTo] = useState(0);
  /** When set, footer shows the mobile-style upload bar (attach + send) for this doc index */
  const [showUploadOptionsForDocIndex, setShowUploadOptionsForDocIndex] = useState<number | null>(null);
  /** File selected via attach for current document; Send button uploads it */
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  /** Back-side file when upload mode is separate/camera */
  const [selectedFileBack, setSelectedFileBack] = useState<File | null>(null);
  /** How card-style docs are uploaded: combined PDF/image or front+back separately */
  const [uploadMode, setUploadMode] = useState<VariousCopyUploadMode | null>(null);
  /** Show mode picker when attach is tapped on a both-sides doc */
  const [showUploadModeModal, setShowUploadModeModal] = useState(false);
  /** Which slot the hidden file input fills */
  const [attachTarget, setAttachTarget] = useState<'combined' | 'front' | 'back'>('combined');
  /** In-app camera target side (null = closed) */
  const [cameraSide, setCameraSide] = useState<CardCameraSide | null>(null);
  /** When true, show confirmation modal before uploading the selected file */
  const [showUploadConfirmModal, setShowUploadConfirmModal] = useState(false);
  /** When true, show file preview overlay (from Preview in confirm modal) */
  const [showFilePreview, setShowFilePreview] = useState(false);
  /** Object URL for the file being previewed; revoke when closing */
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  /** Shown in confirm modal when upload returns 400 or fails */
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const docRequiresBothSides = useCallback(
    (docIndex: number): boolean => {
      const doc = flow?.documents[docIndex];
      if (!doc) return false;
      return doc.requiresBothSides === true || requiresBothSidesUpload(doc.docCode);
    },
    [flow]
  );

  const resetUploadSelection = useCallback(() => {
    setSelectedFile(null);
    setSelectedFileBack(null);
    setUploadMode(null);
    setShowUploadModeModal(false);
    setAttachTarget('combined');
    setCameraSide(null);
  }, []);

  const openNativeFilePicker = useCallback((target: 'combined' | 'front' | 'back', acceptImagesOnly: boolean) => {
    const input = fileInputRef.current;
    if (!input) return;
    input.accept = acceptImagesOnly ? 'image/*' : '.pdf,image/*';
    input.removeAttribute('capture');
    setAttachTarget(target);
    input.value = '';
    input.click();
  }, []);

  const openFilePicker = (target: 'combined' | 'front' | 'back', mode: VariousCopyUploadMode | null) => {
    if (mode === 'camera' && (target === 'front' || target === 'back')) {
      setAttachTarget(target);
      setCameraSide(target);
      return;
    }
    openNativeFilePicker(target, false);
  };

  const handleAttachClick = () => {
    if (showUploadOptionsForDocIndex == null || !flow) return;
    if (!docRequiresBothSides(showUploadOptionsForDocIndex)) {
      openFilePicker('combined', null);
      return;
    }
    if (!uploadMode) {
      setShowUploadModeModal(true);
      return;
    }
    if (uploadMode === 'combined') {
      openFilePicker('combined', 'combined');
      return;
    }
    if (!selectedFile) {
      openFilePicker('front', uploadMode);
    } else if (!selectedFileBack) {
      openFilePicker('back', uploadMode);
    } else {
      openFilePicker('front', uploadMode);
    }
  };

  const handleSelectUploadMode = (mode: VariousCopyUploadMode) => {
    setUploadMode(mode);
    setShowUploadModeModal(false);
    if (mode === 'combined') {
      openFilePicker('combined', mode);
    } else if (mode === 'camera') {
      openFilePicker('front', mode);
    }
  };

  const handleCameraCaptured = (file: File) => {
    const side = cameraSide;
    setCameraSide(null);
    if (side === 'back') {
      setSelectedFileBack(file);
    } else {
      setSelectedFile(file);
      // Guide user to back next if missing
      if (!selectedFileBack) {
        setTimeout(() => setCameraSide('back'), 200);
      }
    }
  };

  const scrollToBottom = useCallback(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  const getChoiceLabel = (status: 'send_later' | 'bring_when_boarding' | 'notyet' | 'skipped') => {
    switch (status) {
      case 'notyet':
        return t('submitDocuments.chat.submitThisTime');
      case 'send_later':
        return t('submitDocuments.chat.sendLater');
      case 'bring_when_boarding':
        return t('submitDocuments.chat.bringWhenBoarding');
      case 'skipped':
        return t('submitDocuments.chat.doNotHaveDocument');
      default:
        return '';
    }
  };

  const handleDocumentChoice = async (
    status: 'send_later' | 'bring_when_boarding' | 'notyet' | 'skipped'
  ) => {
    const email = getEmployeeEmail();
    if (!email || !flow || choiceBusy) return;
    const doc = flow.documents[currentDocIndex];
    if (!doc) return;
    setChoiceBusy(true);
    const ok = await postChatFlowDocumentChoice(
      email,
      flow.invitationId,
      doc.docCode,
      status
    );
    setChoiceBusy(false);
    if (!ok) return;
    if (status === 'notyet') {
      resetUploadSelection();
      setShowUploadOptionsForDocIndex(currentDocIndex);
      if (docRequiresBothSides(currentDocIndex)) {
        setShowUploadModeModal(true);
      }
      const data = await fetchChatFlow(email, language);
      if (data) setFlow(data);
      setTimeout(scrollToBottom, 100);
      return;
    }
    const label = getChoiceLabel(status);
    setChoiceHistory((prev) => [
      ...prev,
      { docIndex: currentDocIndex, label, choiceStatus: status, docCode: doc.docCode },
    ]);
    const data = await fetchChatFlow(email, language);
    if (data) setFlow(data);
    const nextIndex = currentDocIndex + 1;
    const docCount = data?.documents?.length ?? flow.documents.length;
    if (nextIndex >= docCount) {
      setCurrentDocIndex(nextIndex);
      setAssistantRevealedUpTo(nextIndex);
      setAfterReadyStep(4);
      setTimeout(scrollToBottom, 100);
      return;
    }
    setCurrentDocIndex(nextIndex);
    setAfterReadyStep(3);
    setTimeout(scrollToBottom, 100);
    const revealDelayMs = 700;
    setTimeout(() => setAssistantRevealedUpTo(nextIndex), revealDelayMs);
  };

  /** When user selects a file from the picker, store it; upload happens when they tap Send. */
  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || showUploadOptionsForDocIndex == null) return;
    if (attachTarget === 'back') {
      setSelectedFileBack(file);
    } else {
      setSelectedFile(file);
    }
  };

  const canSubmitUpload = (): boolean => {
    if (!selectedFile || showUploadOptionsForDocIndex == null || !flow) return false;
    const separate =
      docRequiresBothSides(showUploadOptionsForDocIndex) &&
      (uploadMode === 'separate' || uploadMode === 'camera');
    if (separate) return Boolean(selectedFileBack);
    return true;
  };

  /** When user taps Send: if file selected show confirmation modal; otherwise open file picker. */
  const handleSendUpload = () => {
    if (!flow || showUploadOptionsForDocIndex == null) return;
    if (!canSubmitUpload()) {
      if (
        docRequiresBothSides(showUploadOptionsForDocIndex) &&
        (uploadMode === 'separate' || uploadMode === 'camera')
      ) {
        if (!selectedFile) openFilePicker('front', uploadMode);
        else if (!selectedFileBack) openFilePicker('back', uploadMode);
      } else {
        handleAttachClick();
      }
      return;
    }
    setShowUploadConfirmModal(true);
  };

  const handleOpenPreview = (file?: File | null) => {
    const target = file ?? selectedFile;
    if (!target) return;
    setPreviewFile(target);
    const url = URL.createObjectURL(target);
    setPreviewObjectUrl(url);
    setShowFilePreview(true);
  };

  const handleClosePreview = useCallback(() => {
    setShowFilePreview(false);
    setPreviewFile(null);
    setPreviewObjectUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return null;
    });
  }, []);

  // Revoke preview object URL on unmount if still set
  useEffect(() => {
    return () => {
      if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
    };
  }, [previewObjectUrl]);

  /** Perform the actual upload after user confirms in the modal. */
  const handleConfirmUpload = async () => {
    const email = getEmployeeEmail();
    if (!email || !flow || showUploadOptionsForDocIndex == null || !selectedFile || choiceBusy) return;
    const doc = flow.documents[showUploadOptionsForDocIndex];
    if (!doc) return;
    setChoiceBusy(true);
    setUploadError(null);
    setShowFilePreview(false);
    setPreviewObjectUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return null;
    });
    const separate =
      docRequiresBothSides(showUploadOptionsForDocIndex) &&
      (uploadMode === 'separate' || uploadMode === 'camera');
    const result = await postChatFlowDocumentUpload(
      email,
      flow.invitationId,
      doc.docCode,
      selectedFile,
      separate && selectedFileBack
        ? { fileBack: selectedFileBack, uploadMode: uploadMode ?? 'separate' }
        : uploadMode
          ? { uploadMode }
          : undefined
    );
    setChoiceBusy(false);
    if (!result.ok) {
      const message =
        result.error && t(`submitDocuments.chat.uploadError_${result.error}`) !== `submitDocuments.chat.uploadError_${result.error}`
          ? t(`submitDocuments.chat.uploadError_${result.error}`)
          : t('submitDocuments.chat.uploadError');
      setUploadError(message);
      return;
    }
    setShowUploadConfirmModal(false);
    const fileName = separate && selectedFileBack
      ? `${selectedFile.name} / ${selectedFileBack.name}`
      : selectedFile.name;
    resetUploadSelection();
    setShowUploadOptionsForDocIndex(null);
    const docName =
      (language === 'en' || language === 'vi' ? doc.nameJa || doc.name : doc.name) ||
      doc.docCode ||
      `Document ${showUploadOptionsForDocIndex + 1}`;
    setChoiceHistory((prev) => [
      ...prev,
      {
        docIndex: showUploadOptionsForDocIndex,
        label: getChoiceLabel('notyet'),
        fileName,
        docName,
        docCode: doc.docCode,
        choiceStatus: 'notyet',
      },
    ]);
    const data = await fetchChatFlow(email, language);
    if (data) setFlow(data);
    const nextIndex = showUploadOptionsForDocIndex + 1;
    const docCount = data?.documents?.length ?? flow.documents.length;
    if (nextIndex >= docCount) {
      setCurrentDocIndex(nextIndex);
      setAssistantRevealedUpTo(nextIndex);
      setAfterReadyStep(4);
      setTimeout(scrollToBottom, 100);
      return;
    }
    setCurrentDocIndex(nextIndex);
    setAfterReadyStep(3);
    setTimeout(scrollToBottom, 100);
    setTimeout(() => setAssistantRevealedUpTo(nextIndex), 700);
  };

  const handleUndoChoice = async () => {
    const email = getEmployeeEmail();
    if (!email || !flow || choiceBusy || choiceHistory.length === 0) return;
    const last = choiceHistory[choiceHistory.length - 1];
    const doc = flow.documents[last.docIndex];
    if (!doc) return;
    setChoiceBusy(true);
    const ok = await postChatFlowDocumentChoice(
      email,
      flow.invitationId,
      doc.docCode,
      'notyet'
    );
    setChoiceBusy(false);
    if (!ok) return;
    setChoiceHistory((prev) => prev.slice(0, -1));
    setCurrentDocIndex((prev) => prev - 1);
    setAssistantRevealedUpTo((prev) => Math.max(0, prev - 1));
    const data = await fetchChatFlow(email, language);
    if (data) setFlow(data);
    setTimeout(scrollToBottom, 100);
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const email = getEmployeeEmail();
      if (!email) {
        setLoadState('error');
        return;
      }
      setLoadState('loading');
      setRevealedCount(0);
      setShowAnswers(false);
      setReadyConfirmed(false);
      setAfterReadyStep(0);
      setCurrentDocIndex(0);
      setChoiceHistory([]);
      setAssistantRevealedUpTo(0);
      setShowUploadOptionsForDocIndex(null);
      setSelectedFile(null);
      setSelectedFileBack(null);
      setUploadMode(null);
      const data = await fetchChatFlow(email, language);
      if (cancelled) return;
      if (!data) {
        setFlow(null);
        setLoadState('error');
        return;
      }
      if (!data.documents || data.documents.length === 0) {
        setFlow(data);
        setLoadState('empty');
        return;
      }
      setFlow(data);
      setLoadState('ready');

      // Hydrate choice history from flow so previous uploads/choices show after refresh
      const lang = language || 'ja';
      const hydrated: { docIndex: number; label: string; fileName?: string; docName?: string; choiceStatus?: 'send_later' | 'bring_when_boarding' | 'notyet' | 'skipped' }[] = [];
      for (let i = 0; i < data.documents.length; i++) {
        const d = data.documents[i];
        const status = d.status || 'notyet';
        if (status === 'submitted' && d.filePath) {
          const fileName = d.filePath.split('/').pop() || d.filePath;
          const docName =
            (lang === 'en' || lang === 'vi' ? d.nameJa || d.name : d.name) || d.docCode || '';
          hydrated.push({
            docIndex: i,
            label: t('submitDocuments.chat.submitThisTime'),
            fileName,
            docName: docName || `Document ${i + 1}`,
            choiceStatus: 'notyet',
          });
        } else if (status === 'send_later') {
          hydrated.push({
            docIndex: i,
            label: t('submitDocuments.chat.sendLater'),
            choiceStatus: 'send_later',
          });
        } else if (status === 'bring_when_boarding') {
          hydrated.push({
            docIndex: i,
            label: t('submitDocuments.chat.bringWhenBoarding'),
            choiceStatus: 'bring_when_boarding',
          });
        } else if (status === 'skipped') {
          hydrated.push({
            docIndex: i,
            label: t('submitDocuments.chat.doNotHaveDocument'),
            choiceStatus: 'skipped',
          });
        } else {
          break;
        }
      }
      if (hydrated.length > 0) {
        setChoiceHistory(hydrated);
        setCurrentDocIndex(hydrated.length);
        setAssistantRevealedUpTo(hydrated.length);
        setReadyConfirmed(true);
        setAfterReadyStep(hydrated.length >= data.documents.length ? 4 : 3);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [language]);

  // Staggered reveal: after ready/empty/error, reveal segments one by one
  useEffect(() => {
    if (loadState === 'loading') return;

    let totalSegments = 1;
    if (loadState === 'error') totalSegments = 1;
    else if (loadState === 'empty' && flow) totalSegments = 2;
    else if (loadState === 'ready' && flow) totalSegments = 4; // intro, list, scan, ready_question

    setRevealedCount(0);
    setShowAnswers(false);

    const timers: ReturnType<typeof setTimeout>[] = [];

    const schedule = (delay: number, fn: () => void) => {
      timers.push(setTimeout(fn, delay));
    };

    // First bubble quickly
    schedule(300, () => {
      setRevealedCount(1);
      scrollToBottom();
    });

    for (let i = 2; i <= totalSegments; i++) {
      schedule(300 + (i - 1) * BUBBLE_DELAY_MS, () => {
        setRevealedCount(i);
        scrollToBottom();
      });
    }

    // Answers after last bubble
    if (loadState === 'ready' && flow && flow.documents.length > 0) {
      schedule(300 + totalSegments * BUBBLE_DELAY_MS + AFTER_LAST_BUBBLE_MS, () => {
        setShowAnswers(true);
        scrollToBottom();
      });
    } else if (loadState === 'empty' || loadState === 'error') {
      // No answer block for empty/error or show after last stagger — optional: show after empty's 2 bubbles
      if (loadState === 'empty') {
        schedule(300 + totalSegments * BUBBLE_DELAY_MS + AFTER_LAST_BUBBLE_MS, () => setShowAnswers(true));
      }
    }

    return () => timers.forEach(clearTimeout);
  }, [loadState, scrollToBottom]);

  // After はい: stagger user bubble then assistant replies, then show 3-button footer
  useEffect(() => {
    if (!readyConfirmed || !flow || loadState !== 'ready') return;
    setAfterReadyStep(0);
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => setAfterReadyStep(1), 200));
    timers.push(setTimeout(() => setAfterReadyStep(2), 200 + BUBBLE_DELAY_MS));
    timers.push(
      setTimeout(() => {
        setAfterReadyStep(3);
        scrollToBottom();
      }, 200 + 2 * BUBBLE_DELAY_MS)
    );
    return () => timers.forEach(clearTimeout);
  }, [readyConfirmed, loadState, scrollToBottom]);

  // Scroll to bottom when a new "Document N" line is revealed (after React has painted it)
  useEffect(() => {
    if (!readyConfirmed || loadState !== 'ready') return;
    const id = setTimeout(scrollToBottom, 80);
    return () => clearTimeout(id);
  }, [assistantRevealedUpTo, readyConfirmed, loadState, scrollToBottom]);

  const msg = (key: string) => t(`submitDocuments.chat.${key}`) || key;

  return (
    <div className="chat-document-flow">
      <div className="chat-document-flow__card">
        <header className="chat-document-flow__top">
          <button type="button" className="chat-document-flow__cancel-pill" onClick={onBack}>
            <span className="chat-document-flow__cancel-x" aria-hidden>
              ×
            </span>
            {t('submitDocuments.chat.cancelProcedure')}
          </button>
        </header>

        <div className="chat-document-flow__thread-wrap">
          <div className="chat-document-flow__thread" role="log" aria-live="polite">
            {/* Loading: single bubble immediately (no stagger until we have flow) */}
            {loadState === 'loading' && (
              <AssistantRow animateIn>{t('submitDocuments.chat.loading')}</AssistantRow>
            )}

            {loadState === 'error' && revealedCount >= 1 && (
              <AssistantRow variant="warn" animateIn>
                {t('submitDocuments.chat.loadError')}</AssistantRow>
            )}

            {loadState === 'empty' && flow && (
              <>
                {revealedCount >= 1 && (
                  <AssistantRow animateIn>{msg('intro')}</AssistantRow>
                )}
                {revealedCount >= 2 && (
                  <AssistantRow variant="warn" animateIn>
                    {t('submitDocuments.chat.noDocuments')}
                  </AssistantRow>
                )}
              </>
            )}

            {loadState === 'ready' && flow && (
              <>
                {revealedCount >= 1 && (
                  <AssistantRow animateIn>{msg('intro')}</AssistantRow>
                )}
                {revealedCount >= 2 && (
                  <AssistantRow animateIn>
                    <div className="chat-document-flow__list-title">
                      {t('submitDocuments.chat.documentsToPrepare')}
                    </div>
                    <ol className="chat-document-flow__doc-list">
                      {flow.documents.map((doc) => (
                        <li key={doc.docCode} className="chat-document-flow__doc-list-item">
                          {(language === 'en' || language === 'vi') && doc.nameJa ? (
                            <>
                              <span className="chat-document-flow__doc-list-name">{doc.nameJa}</span>
                              <span className="chat-document-flow__doc-list-name-alt"> ({doc.name})</span>
                            </>
                          ) : (
                            <span className="chat-document-flow__doc-list-name">{doc.name}</span>
                          )}
                        </li>
                      ))}
                    </ol>
                  </AssistantRow>
                )}
                {revealedCount >= 3 && (
                  <AssistantRow animateIn>
                    <p className="chat-document-flow__upload-intro">{t('submitDocuments.chat.uploadHowTo')}</p>
                    <div className="chat-document-flow__upload-options">
                      <div className="chat-document-flow__upload-option">
                        <span className="chat-document-flow__upload-option-label">
                          {t('submitDocuments.chat.optionMulticopy')}
                        </span>
                        <a href="#instructions-multicopy" className="chat-document-flow__link">
                          {t('submitDocuments.chat.seeInstructions')}
                        </a>
                      </div>
                      <div className="chat-document-flow__upload-option">
                        <span className="chat-document-flow__upload-option-label">
                          {t('submitDocuments.chat.optionGallery')}
                        </span>
                        <a href="#instructions-gallery" className="chat-document-flow__link">
                          {t('submitDocuments.chat.seeInstructions')}
                        </a>
                      </div>
                      <div className="chat-document-flow__upload-option">
                        <span className="chat-document-flow__upload-option-label">
                          {t('submitDocuments.chat.optionTakePhoto')}
                        </span>
                        <a href="#instructions-camera" className="chat-document-flow__link">
                          {t('submitDocuments.chat.seeInstructions')}
                        </a>
                      </div>
                    </div>
                  </AssistantRow>
                )}
                {revealedCount >= 4 && (
                  <AssistantRow animateIn>{msg('readyQuestion')}</AssistantRow>
                )}
              </>
            )}

            {/* Step D/E: after はい、準備できました — user bubble + assistant + per-doc (user choice → Document N) */}
            {loadState === 'ready' && flow && readyConfirmed && (
              <>
                {afterReadyStep >= 1 && (
                  <div className="chat-document-flow__row chat-document-flow__row--user chat-document-flow__row--enter">
                    <div className="chat-document-flow__bubble chat-document-flow__bubble--user">
                      {t('submitDocuments.chat.yesReady')}
                    </div>
                  </div>
                )}
                {afterReadyStep >= 2 && (
                  <AssistantRow animateIn>{t('submitDocuments.chat.afterReady')}</AssistantRow>
                )}
                {afterReadyStep >= 3 &&
                  flow.documents.length > 0 &&
                  [...Array(currentDocIndex + 1)].map((_, k) => (
                    <React.Fragment key={k}>
                      {k > 0 && choiceHistory[k - 1] && (
                        <div className="chat-document-flow__row chat-document-flow__row--user chat-document-flow__row--enter">
                          <div className="chat-document-flow__user-bubble-wrap">
                            <div className="chat-document-flow__upload-bubble-row">
                              {choiceHistory[k - 1].fileName ? (
                                <>
                                  <img
                                    src={attachIcon}
                                    alt=""
                                    className="chat-document-flow__upload-bubble-attach"
                                    aria-hidden
                                  />
                                  <div className="chat-document-flow__bubble chat-document-flow__bubble--user chat-document-flow__bubble--upload">
                                    <span className="chat-document-flow__upload-bubble-doc">
                                      {String.fromCharCode(0x2460 + (k - 1))} :{' '}
                                      {choiceHistory[k - 1].docName ||
                                        (flow.documents[choiceHistory[k - 1].docIndex]
                                          ? (language === 'en' || language === 'vi')
                                            ? flow.documents[choiceHistory[k - 1].docIndex].nameJa ||
                                              flow.documents[choiceHistory[k - 1].docIndex].name
                                            : flow.documents[choiceHistory[k - 1].docIndex].name
                                          : '') ||
                                        t('submitDocuments.chat.documentPrefix') + (k - 1 + 1)}
                                    </span>
                                    <span className="chat-document-flow__upload-bubble-file">
                                      {choiceHistory[k - 1].fileName}
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <div className="chat-document-flow__bubble chat-document-flow__bubble--user">
                                  {choiceHistory[k - 1].label}
                                </div>
                              )}
                            </div>
                            {k === currentDocIndex && (
                              <button
                                type="button"
                                className="chat-document-flow__undo"
                                onClick={handleUndoChoice}
                                disabled={choiceBusy}
                              >
                                {t('submitDocuments.chat.undo')}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {k > 0 &&
                        choiceHistory[k - 1]?.choiceStatus === 'bring_when_boarding' && (
                          <AssistantRow animateIn>
                            {t('submitDocuments.chat.acknowledgeBringWhenBoarding')}
                          </AssistantRow>
                        )}
                      {k > 0 &&
                        choiceHistory[k - 1]?.choiceStatus === 'send_later' && (
                          <AssistantRow animateIn>
                            {t('submitDocuments.chat.acknowledgeSendLater')}
                          </AssistantRow>
                        )}
                      {k > 0 &&
                        choiceHistory[k - 1]?.fileName &&
                        requiresOriginalToHeadOffice(choiceHistory[k - 1]?.docCode) && (
                          <AssistantRow animateIn>
                            {t('submitDocuments.chat.acknowledgeSendOriginal')}
                          </AssistantRow>
                        )}
                      {flow.documents[k] && assistantRevealedUpTo >= k && (
                        <>
                          <AssistantRow animateIn>
                            {t('submitDocuments.chat.documentPrefix')}{k + 1}：
                            {(language === 'en' || language === 'vi') && flow.documents[k].nameJa ? (
                              <>
                                <span className="chat-document-flow__doc-list-name">{flow.documents[k].nameJa}</span>
                                <span className="chat-document-flow__doc-list-name-alt"> ({flow.documents[k].name})</span>
                              </>
                            ) : (
                              flow.documents[k].name
                            )}{' '}
                            {t('submitDocuments.chat.selectMethod')}
                          </AssistantRow>
                          {(flow.documents[k].description || flow.documents[k].descriptionJa) ? (
                            <AssistantRow animateIn>
                              <p className="chat-document-flow__doc-description">
                                {flow.documents[k].description || flow.documents[k].descriptionJa}
                              </p>
                            </AssistantRow>
                          ) : null}
                        </>
                      )}
                    </React.Fragment>
                  ))}
                {afterReadyStep >= 4 && (
                  <AssistantRow animateIn>
                    <p className="chat-document-flow__completion-thanks">{t('submitDocuments.chat.completionThanks')}</p>
                    <p className="chat-document-flow__completion-done">{t('submitDocuments.chat.completionStep1Done')}</p>
                    {choiceHistory.some(
                      (c) => c.fileName && requiresOriginalToHeadOffice(c.docCode)
                    ) && (
                      <p className="chat-document-flow__completion-done">
                        {t('submitDocuments.chat.completionOriginalReminder')}
                      </p>
                    )}
                    <p className="chat-document-flow__completion-tap">{t('submitDocuments.chat.completionTapButton')}</p>
                  </AssistantRow>
                )}
              </>
            )}

            <div ref={threadEndRef} className="chat-document-flow__thread-end" aria-hidden />
          </div>
        </div>

        {showAnswers && loadState === 'ready' && flow && flow.documents.length > 0 && !readyConfirmed && (
          <footer className="chat-document-flow__footer chat-document-flow__footer--enter">
            <div className="chat-document-flow__answers-block">
            <button
                  type="button"
                  className="chat-document-flow__answer-row"
                  onClick={() => {
                    setReadyConfirmed(true);
                    setShowAnswers(false); // hide old footer while new messages appear
                    onReadyConfirmed?.();
                    // optional: scroll after a tick so new bubbles are in view
                    setTimeout(scrollToBottom, 100);
                  }}
                >
                  {t('submitDocuments.chat.yesReady')}
                </button>
              <hr className="chat-document-flow__answer-sep" aria-hidden />
              <button type="button" className="chat-document-flow__answer-row" onClick={onBack}>
                {t('submitDocuments.chat.returnAfterScan')}
              </button>
            </div>
          </footer>
        )}

        {/* Step 4: all documents done — show Continue button */}
        {readyConfirmed &&
          loadState === 'ready' &&
          flow &&
          flow.documents.length > 0 &&
          afterReadyStep >= 4 && (
            <footer className="chat-document-flow__footer chat-document-flow__footer--enter">
              <div className="chat-document-flow__answers-block">
                <button
                  type="button"
                  className="chat-document-flow__continue-btn"
                  onClick={() => onContinue?.()}
                >
                  <span className="chat-document-flow__continue-btn-text">{t('submitDocuments.chat.continue')}</span>
                  <span className="chat-document-flow__continue-btn-arrow-wrap" aria-hidden>
                    <img src={continueIcon} alt="" className="chat-document-flow__continue-btn-arrow" />
                  </span>
                </button>
              </div>
            </footer>
          )}

        {/* Step E: submission options, or (when Submit now chosen) mobile-style upload bar */}
        {readyConfirmed &&
          loadState === 'ready' &&
          flow &&
          flow.documents.length > 0 &&
          afterReadyStep === 3 && (
            <footer className="chat-document-flow__footer chat-document-flow__footer--enter">
              <div className="chat-document-flow__answers-block">
                {showUploadOptionsForDocIndex != null ? (
                  <div className="chat-document-flow__upload-bar">
                    {docRequiresBothSides(showUploadOptionsForDocIndex) &&
                    (uploadMode === 'separate' || uploadMode === 'camera') ? (
                      <>
                        <p className="chat-document-flow__dual-upload-hint">
                          {t('submitDocuments.chat.dualUploadSeparateHint')}
                        </p>
                        <div className="chat-document-flow__dual-upload-slots">
                          <div className="chat-document-flow__dual-upload-slot">
                            <span className="chat-document-flow__dual-upload-slot-label">
                              {t('submitDocuments.chat.uploadFrontSide')}
                            </span>
                            <div className="chat-document-flow__upload-bar-inner">
                              <span className="chat-document-flow__upload-bar-placeholder">
                                {selectedFile?.name ?? t('submitDocuments.chat.uploadFrontPlaceholder')}
                              </span>
                              <button
                                type="button"
                                className="chat-document-flow__upload-bar-attach"
                                onClick={() => openFilePicker('front', uploadMode)}
                                disabled={choiceBusy}
                                aria-label={t('submitDocuments.chat.attachFile')}
                              >
                                <img src={attachIcon} alt="" aria-hidden />
                              </button>
                            </div>
                          </div>
                          <div className="chat-document-flow__dual-upload-slot">
                            <span className="chat-document-flow__dual-upload-slot-label">
                              {t('submitDocuments.chat.uploadBackSide')}
                            </span>
                            <div className="chat-document-flow__upload-bar-inner">
                              <span className="chat-document-flow__upload-bar-placeholder">
                                {selectedFileBack?.name ?? t('submitDocuments.chat.uploadBackPlaceholder')}
                              </span>
                              <button
                                type="button"
                                className="chat-document-flow__upload-bar-attach"
                                onClick={() => openFilePicker('back', uploadMode)}
                                disabled={choiceBusy}
                                aria-label={t('submitDocuments.chat.attachFile')}
                              >
                                <img src={attachIcon} alt="" aria-hidden />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="chat-document-flow__upload-bar-row chat-document-flow__upload-bar-row--actions">
                          <button
                            type="button"
                            className="chat-document-flow__upload-bar-send"
                            onClick={handleSendUpload}
                            disabled={choiceBusy}
                            aria-label={t('submitDocuments.chat.sendUpload')}
                          >
                            <img src={sendIcon} alt="" aria-hidden />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="chat-document-flow__upload-bar-row">
                        <div className="chat-document-flow__upload-bar-inner">
                          <span className="chat-document-flow__upload-bar-placeholder">
                            {selectedFile
                              ? selectedFile.name
                              : (() => {
                                  const doc = flow.documents[showUploadOptionsForDocIndex];
                                  const docName = doc
                                    ? (language === 'en' || language === 'vi') && doc.nameJa
                                      ? doc.nameJa
                                      : doc.name
                                    : '';
                                  const suffix = t('submitDocuments.chat.uploadPlaceholderSuffix');
                                  return docName ? `${docName}${suffix}` : suffix;
                                })()}
                          </span>
                          <button
                            type="button"
                            className="chat-document-flow__upload-bar-attach"
                            onClick={handleAttachClick}
                            disabled={choiceBusy}
                            aria-label={t('submitDocuments.chat.attachFile')}
                          >
                            <img src={attachIcon} alt="" aria-hidden />
                          </button>
                        </div>
                        <button
                          type="button"
                          className="chat-document-flow__upload-bar-send"
                          onClick={handleSendUpload}
                          disabled={choiceBusy}
                          aria-label={t('submitDocuments.chat.sendUpload')}
                        >
                          <img src={sendIcon} alt="" aria-hidden />
                        </button>
                      </div>
                    )}
                    {docRequiresBothSides(showUploadOptionsForDocIndex) && uploadMode && (
                      <button
                        type="button"
                        className="chat-document-flow__upload-bar-change-mode"
                        onClick={() => {
                          setSelectedFile(null);
                          setSelectedFileBack(null);
                          setUploadMode(null);
                          setShowUploadModeModal(true);
                        }}
                        disabled={choiceBusy}
                      >
                        {t('submitDocuments.chat.uploadModeTitle')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="chat-document-flow__upload-bar-cancel"
                      onClick={() => {
                        setShowUploadOptionsForDocIndex(null);
                        resetUploadSelection();
                      }}
                      disabled={choiceBusy}
                    >
                      {t('submitDocuments.chat.cancelUploadOption')}
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="chat-document-flow__answer-row"
                      disabled={choiceBusy}
                      onClick={() => handleDocumentChoice('notyet')}
                    >
                      {t('submitDocuments.chat.submitThisTime')}
                    </button>
                    <hr className="chat-document-flow__answer-sep" aria-hidden />
                    <button
                      type="button"
                      className="chat-document-flow__answer-row"
                      disabled={choiceBusy}
                      onClick={() => handleDocumentChoice('send_later')}
                    >
                      {t('submitDocuments.chat.sendLater')}
                    </button>
                    <hr className="chat-document-flow__answer-sep" aria-hidden />
                    <button
                      type="button"
                      className="chat-document-flow__answer-row"
                      disabled={choiceBusy}
                      onClick={() => handleDocumentChoice('bring_when_boarding')}
                    >
                      {t('submitDocuments.chat.bringWhenBoarding')}
                    </button>
                    {allowsVariousCopySkip(flow.documents[currentDocIndex]?.docCode) && (
                      <>
                        <hr className="chat-document-flow__answer-sep" aria-hidden />
                        <button
                          type="button"
                          className="chat-document-flow__answer-row"
                          disabled={choiceBusy}
                          onClick={() => handleDocumentChoice('skipped')}
                        >
                          {t('submitDocuments.chat.doNotHaveDocument')}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </footer>
          )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,image/*"
          onChange={handleFileSelected}
          style={{ display: 'none' }}
          aria-hidden
        />

        {/* Upload mode choice (card docs with front+back) */}
        {showUploadModeModal && showUploadOptionsForDocIndex != null && (
          <div
            className="chat-document-flow__confirm-overlay"
            onClick={() => setShowUploadModeModal(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="upload-mode-title"
          >
            <div
              className="chat-document-flow__confirm-modal chat-document-flow__mode-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="chat-document-flow__confirm-header">
                <h2 id="upload-mode-title" className="chat-document-flow__confirm-title">
                  {t('submitDocuments.chat.uploadModeTitle')}
                </h2>
              </div>
              <div className="chat-document-flow__confirm-body">
                <p className="chat-document-flow__confirm-message">
                  {t('submitDocuments.chat.uploadModeHint')}
                </p>
                <div className="chat-document-flow__mode-options">
                  <button
                    type="button"
                    className="chat-document-flow__mode-option"
                    onClick={() => handleSelectUploadMode('combined')}
                  >
                    {t('submitDocuments.chat.uploadModeCombined')}
                  </button>
                  <button
                    type="button"
                    className="chat-document-flow__mode-option"
                    onClick={() => handleSelectUploadMode('separate')}
                  >
                    {t('submitDocuments.chat.uploadModeSeparate')}
                  </button>
                  {isMobileDevice() && (
                    <button
                      type="button"
                      className="chat-document-flow__mode-option"
                      onClick={() => handleSelectUploadMode('camera')}
                    >
                      {t('submitDocuments.chat.uploadModeCamera')}
                    </button>
                  )}
                </div>
              </div>
              <div className="chat-document-flow__confirm-actions">
                <button
                  type="button"
                  className="chat-document-flow__confirm-btn chat-document-flow__confirm-btn--cancel"
                  onClick={() => setShowUploadModeModal(false)}
                >
                  {t('submitDocuments.chat.confirmCancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload confirmation modal */}
        {showUploadConfirmModal && flow && selectedFile && showUploadOptionsForDocIndex != null && (
          <div
            className="chat-document-flow__confirm-overlay"
            onClick={() => {
              if (!choiceBusy) {
                setShowUploadConfirmModal(false);
                setUploadError(null);
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="upload-confirm-title"
          >
            <div
              className="chat-document-flow__confirm-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="chat-document-flow__confirm-header">
                <h2 id="upload-confirm-title" className="chat-document-flow__confirm-title">
                  {t('submitDocuments.chat.confirmTitle')}
                </h2>
                <img src={cautionIcon} alt="" className="chat-document-flow__confirm-icon" aria-hidden />
              </div>
              <div className="chat-document-flow__confirm-body">
                {uploadError && (
                  <p className="chat-document-flow__confirm-error" role="alert">
                    {uploadError}
                  </p>
                )}
                <p className="chat-document-flow__confirm-message">
                  {(() => {
                    const doc = flow.documents[showUploadOptionsForDocIndex];
                    const docName = doc
                      ? (language === 'en' || language === 'vi') && doc.nameJa
                        ? doc.nameJa
                        : doc.name
                      : '';
                    const suffix = t('submitDocuments.chat.confirmMessageSuffix');
                    return docName ? `${docName} ${suffix}` : suffix;
                  })()}
                </p>
                <p className="chat-document-flow__confirm-hint">
                  {t('submitDocuments.chat.confirmReuploadHint')}
                </p>
                <div className="chat-document-flow__confirm-file-row">
                  <span className="chat-document-flow__confirm-filename">{selectedFile.name}</span>
                  <button
                    type="button"
                    className="chat-document-flow__confirm-preview"
                    onClick={() => handleOpenPreview(selectedFile)}
                    disabled={choiceBusy}
                    aria-label={t('submitDocuments.chat.preview')}
                  >
                    <span className="chat-document-flow__confirm-preview-icon" aria-hidden />
                    {t('submitDocuments.chat.preview')}
                  </button>
                </div>
                {selectedFileBack && (
                  <div className="chat-document-flow__confirm-file-row">
                    <span className="chat-document-flow__confirm-filename">
                      {t('submitDocuments.chat.uploadBackSide')}: {selectedFileBack.name}
                    </span>
                    <button
                      type="button"
                      className="chat-document-flow__confirm-preview"
                      onClick={() => handleOpenPreview(selectedFileBack)}
                      disabled={choiceBusy}
                      aria-label={t('submitDocuments.chat.preview')}
                    >
                      <span className="chat-document-flow__confirm-preview-icon" aria-hidden />
                      {t('submitDocuments.chat.preview')}
                    </button>
                  </div>
                )}
              </div>
              <div className="chat-document-flow__confirm-actions">
                <button
                  type="button"
                  className="chat-document-flow__confirm-btn chat-document-flow__confirm-btn--cancel"
                  onClick={() => {
                    setShowUploadConfirmModal(false);
                    setUploadError(null);
                  }}
                  disabled={choiceBusy}
                >
                  {t('submitDocuments.chat.confirmCancel')}
                </button>
                <span className="chat-document-flow__confirm-actions-sep" aria-hidden />
                <button
                  type="button"
                  className="chat-document-flow__confirm-btn chat-document-flow__confirm-btn--upload"
                  onClick={handleConfirmUpload}
                  disabled={choiceBusy}
                >
                  {t('submitDocuments.chat.confirmUpload')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* File preview overlay (when user clicks Preview in confirm modal) */}
        {showFilePreview && previewObjectUrl && previewFile && (
          <div
            className="chat-document-flow__preview-overlay"
            onClick={handleClosePreview}
            role="dialog"
            aria-modal="true"
            aria-label={t('submitDocuments.chat.preview')}
          >
            <div
              className="chat-document-flow__preview-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="chat-document-flow__preview-close"
                onClick={handleClosePreview}
                aria-label={t('submitDocuments.chat.closeInstruction')}
              >
                ×
              </button>
              <div className="chat-document-flow__preview-body">
                {previewFile.type.startsWith('image/') ? (
                  <img
                    src={previewObjectUrl}
                    alt={previewFile.name}
                    className="chat-document-flow__preview-img"
                  />
                ) : previewFile.type === 'application/pdf' || previewFile.name.toLowerCase().endsWith('.pdf') ? (
                  previewObjectUrl ? (
                    <PdfCanvasViewer
                      pdfUrl={previewObjectUrl}
                      title={previewFile.name}
                      className="chat-document-flow__preview-pdf"
                    />
                  ) : null
                ) : (
                  <p className="chat-document-flow__preview-fallback">
                    {t('submitDocuments.chat.previewNotAvailable')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {showAnswers && loadState === 'empty' && (
          <footer className="chat-document-flow__footer chat-document-flow__footer--enter">
            <div className="chat-document-flow__answers-block">
              <button type="button" className="chat-document-flow__answer-row" onClick={onBack}>
                {t('submitDocuments.back')}
              </button>
            </div>
          </footer>
        )}
      </div>

      {cameraSide && (
        <CardCameraCapture
          side={cameraSide}
          sideLabel={
            cameraSide === 'back'
              ? t('submitDocuments.chat.uploadBackSide')
              : t('submitDocuments.chat.uploadFrontSide')
          }
          tipText={t('submitDocuments.chat.cameraTip')}
          captureLabel={t('submitDocuments.chat.cameraCapture')}
          usePhotoLabel={t('submitDocuments.chat.cameraUsePhoto')}
          retakeLabel={t('submitDocuments.chat.cameraRetake')}
          cancelLabel={t('submitDocuments.chat.cameraCancel')}
          errorPermissionLabel={t('submitDocuments.chat.cameraErrorPermission')}
          errorGenericLabel={t('submitDocuments.chat.cameraErrorGeneric')}
          fallbackLabel={t('submitDocuments.chat.cameraFallback')}
          onCaptured={handleCameraCaptured}
          onCancel={() => setCameraSide(null)}
          onFallback={() => {
            const target = cameraSide;
            setCameraSide(null);
            openNativeFilePicker(target, true);
          }}
        />
      )}
    </div>
  );
};

export default ChatDocumentFlow;
